import {Injectable} from '@angular/core';
import {NavItem} from '../models/nav-item.model';
import {ActivatedRouteSnapshot} from '@angular/router';
import {Store} from '@ngxs/store';
import {Navigate} from '@ngxs/router-plugin';
import {SelectSnapshot} from '@ngxs-labs/select-snapshot';
import {NavPartnerFactory} from './nav/partner.factory';
import {NavPasswordPolicyFactory} from './nav/password-policy.factory';
import {NavRootFactory} from './nav/root.factory';
import {NavLocalizationFactory} from './nav/localization.factory';
import {NavFactory} from './nav/nav.factory';
import {NavUserFactory} from './nav/user.factory';
import {NavPolicyFactory} from './nav/policy.factory';
import {NavProductFactory} from './nav/product.factory';
import {NavDocumentFactory} from './nav/document.factory';
import {NavConfigFactory} from './nav/config.factory';
import {NavSalesCpiFactory} from './nav/sales-cpi.factory';
import {NavItemCodes} from '../enums/nav-item-codes.enum';
import {NavSalesPgFactory} from './nav/sales-pg.factory';
import {NavReportFactory} from './nav/report.factory';
import {ActiveProducts} from '../models/product.model';
import {ActiveProductState} from '../state/active-products/active-products.state';
import {SystemUser} from '../models/system-user.model';
import {NavMigratedPoliciesFactory} from './nav/migrated-policies.factory';
import {AuthState} from '../state/auth/auth.state';
import {NavRepositoryPoliciesFactory} from './nav/repository-policies.factory';

@Injectable()
export class NavService extends NavFactory {

    constructor(private store: Store) {
        super();
    }

    protected static items: NavItem[] = [
        ...NavRootFactory.getAll(),
        ...NavDocumentFactory.getAll(),
        ...NavLocalizationFactory.getAll(),
        ...NavPartnerFactory.getAll(),
        ...NavPasswordPolicyFactory.getAll(),
        ...NavPolicyFactory.getAll(),
        ...NavProductFactory.getAll(),
        ...NavUserFactory.getAll(),
        ...NavConfigFactory.getAll(),
        ...NavSalesCpiFactory.getAll(),
        ...NavSalesPgFactory.getAll(),
        ...NavReportFactory.getAll(),
        ...NavMigratedPoliciesFactory.getAll(),
        ...NavRepositoryPoliciesFactory.getAll(),
    ];

    @SelectSnapshot(ActiveProductState.activeProducts) activeProducts: ActiveProducts;
    @SelectSnapshot(AuthState.currentUser) currentUser: SystemUser;

    static getNavItemMap(items): { [id: string]: NavItem } {
        const result = {};
        items.forEach(({code, props}) => result[code] = NavService.get(code, props));
        return result;
    }

    static getBreadcrumbs(item: ActivatedRouteSnapshot | null, currentUser: SystemUser): NavItem[] {
        let breadcrumb;
        const breadcrumbCode = item?.data?.breadcrumb && typeof item.data.breadcrumb === 'function'
            ? item.data.breadcrumb(currentUser)
            : item?.data?.breadcrumb;
        if (breadcrumbCode) {
            const navItem = NavService.get(breadcrumbCode);
            breadcrumb = item.params
                ? new NavItem({
                    ...navItem,
                    path: navItem.getPath(item.params) || navItem.path
                })
                : new NavItem(navItem);
        }
        if (item.firstChild) {
            return [breadcrumb, ...this.getBreadcrumbs(item.firstChild, currentUser)];
        }
        return [breadcrumb];
    }

    getRootNavItems(shorterLables = false): NavItem[] {
        const managementItem = ((this.currentUser?.role?.code === 'ADMIN_PARTNER') || (this.currentUser?.role?.code === 'ADMIN_ALLEGRO'))
            ? NavService.get(NavItemCodes.LOCALIZATION_MANAGEMENT_LIMITED, {payload: {partnerId: this.currentUser?.partner?.id}})
            : NavService.get(NavItemCodes.MANAGEMENT);
        return [
            NavService.get(NavItemCodes.SALES_CPI_PRODUCT_PICK),
            NavService.get(NavItemCodes.POLICY_ADD),
            NavService.get(shorterLables ? NavItemCodes.POLICY_LABEL_SHORT : NavItemCodes.POLICY_REGISTRY),
            NavService.get(shorterLables ? NavItemCodes.MIGRATED_POLICIES_LABEL_SHORT : NavItemCodes.MIGRATED_POLICIES),
            NavService.get(shorterLables ? NavItemCodes.REPOSITORY_LABEL_SHORT : NavItemCodes.REPOSITORY_LABEL_BROKEN),
            NavService.get(NavItemCodes.DOCUMENT_VIEW),
            managementItem,
            NavService.get(NavItemCodes.REPORTS_LIST),
        ];
    }

    getRootNavItemsFiltered(shorterLables = false): NavItem[] {
        return this.getRootNavItems(shorterLables)
            .filter(item => {
                if (item.code === NavItemCodes.SALES_CPI_PRODUCT_PICK) {
                    return this.activeProducts.cpi;
                } else if (item.code === NavItemCodes.POLICY_ADD) {
                    return this.activeProducts.pg;
                }
                return true;
            });
    }

    handleClick(item: NavItem, payload = {}) {
        if (item.action) {
            this.handleDispatch(item, payload);
        }
        if (item.path) {
            this.handleNavigate(item, payload);
        }
    }

    handleDispatch(item: NavItem, payload = {}) {
        const action = item.getAction(payload);
        if (action) {
            this.store.dispatch(action);
        }
    }

    handleNavigate(item: NavItem, payload = {}) {
        const path = item.getPath(payload);
        if (path) {
            this.store.dispatch(new Navigate(path));
        }
    }

    navigateByCode(code: NavItemCodes, payload = {}) {
        const definition = NavService.get(code);
        if (definition && definition.path) {
            const item = new NavItem({...definition});
            this.handleNavigate(item, payload);
        }
    }
}
